import * as Select from "@radix-ui/react-select";
import * as stylex from "@stylexjs/stylex";
import { getThemeLabel } from "components/article-switcher";
import { getFocusMetadata } from "focus-content";
import { motion } from "framer-motion";
import { useRTL } from "hooks";
import useLocale from "hooks/useLocale";
import { ArrowLeft14 } from "icons/ArrowLeft";
import { Article16 } from "icons/Article";
import Link from "next/link";
import React from "react";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { subthemesIndex, themeAliases } from "routes";
import { useImmer } from "use-immer";
import { focusArticles } from "../../focus";
import * as M from "../../materials";
import { breakpoints, colors } from "../../materials/consts.stylex";
import { typography } from "../../materials/typography.stylex";
import { rowStyles, sectionStyles, stylexStyles } from "./shared/table";
const linkArrow = new URL("../../icons/svg/ic_link_24dp.svg", import.meta.url).href;
const styles = {
    rootTitle: {
        k1K539: "x1hq5gj4",
        keoZOQ: "xw7yly9",
        kmVPX3: "x1xq1gxn x17a8j0p",
        $$css: true
    },
    lead: {
        kMwMTN: "x196nufh",
        k1xSpc: "x104kibb",
        kJFfOR: "xh0615m",
        kgKLqz: "x1ua5tub",
        kVQacm: "xb3r6kr",
        kg5iWk: "xlyipyv",
        $$css: true
    },
    expandButton: {
        k3XXqK: "x1t137rt",
        kWkggS: "xjbqb8w",
        ksu8eU: "xng3xce",
        kzqmXN: "xh8yej3",
        kkrTdU: "x1ypdohk",
        kmkexE: "x1fbg24j",
        k63SB2: "x1s688f",
        kMwMTN: "x12klvby",
        kDPRdz: "xhr9w20",
        k9WMMc: "x2b8uid",
        k1xSpc: "x78zum5",
        kjj79g: "xl56j7k",
        $$css: true
    }
};
export function FocusIndex() {
    const isRTL = useRTL();
    const locale = useLocale();
    const [state, setState] = useImmer({
        expanded: {
            access: false,
            equity: false,
            learning: false,
            quality: false,
            finance: false
        },
        publication: "all"
    });
    const intl = useIntl();
    const focuses = React.useMemo(()=>focusArticles.published.map((a)=>getFocusMetadata(a, locale)), [
        locale
    ]);
    const themesFocus = React.useMemo(()=>{
        return themeAliases.map((theme)=>{
            const subthemes = subthemesIndex[theme];
            return {
                alias: theme,
                focuses: focuses.filter((focus)=>subthemes.includes(focus.article) && (focus.year === state.publication || state.publication === "all")).sort((a, b)=>a.year > b.year ? -1 : 1)
            };
        });
    }, [
        focuses,
        state.publication
    ]);
    const publications = React.useMemo(()=>{
        const years = [
            ...new Set(focuses.map((focus)=>focus.year).sort((a, b)=>a > b ? -1 : 1))
        ];
        const pubs = years.map((year)=>({
                label: `${intl.formatMessage(defineMessage({
                    id: "GEM Report",
                    defaultMessage: "GEM Report"
                }))} ${year}`,
                value: year
            }));
        return [
            {
                label: intl.formatMessage(defineMessage({
                    id: "All Publications",
                    defaultMessage: "All Publications"
                })),
                value: "all"
            },
            ...pubs
        ];
    }, [
        focuses,
        intl
    ]);
    const childVariants = {
        hover: {
            x: isRTL ? -5 : 5,
            rotate: isRTL ? 180 : 0
        },
        default: {
            x: 0,
            rotate: isRTL ? 180 : 0
        }
    };
    return <div>
      <PublicationSelect options={publications} value={state.publication} onChange={(v)=>{
        setState((draft)=>{
            draft.publication = v;
        });
    }}/>
      <motion.div layout>
        {themesFocus.map((theme)=>{
        if (theme.focuses.length === 0) return null;
        const focusToDisplay = state.expanded[theme.alias] ? theme.focuses.length : 2;
        return <motion.div key={theme.alias} {...stylex.props(sectionStyles.root)} layoutId={`section-${theme.alias}`}>
              <div {...stylex.props(stylexStyles.table)}>
                <div {...stylex.props(sectionStyles.content, isRTL ? sectionStyles.contentRtl : sectionStyles.contentLtr)}>
                  <motion.h2 layout="position" {...stylex.props(typography.heading2, styles.rootTitle)}>
                    {getThemeLabel(theme.alias)}
                  </motion.h2>
                  {theme.focuses.slice(0, focusToDisplay).map((focus)=>{
            return <Link href={`/focus/${focus.alias}`} key={focus.alias}>
                        {" "}
                        <motion.div {...stylex.props(rowStyles.row, rowStyles.rowLink)} layoutId={`row-${focus.alias}`}>
                          <div className="x78zum5 x1a02dak x1xq1gxn xou54vl x6s0dn4 x1nhvcw1">
                            <Article16 className="x2lah0s x1td3qas x10w6t97" style={{
                color: M.themes[theme.alias].palette[5]
            }}/>
                            <div className="xxdlds1">
                              <h4 {...stylex.props(typography.heading4)}>{focus.title}</h4>

                              <p {...stylex.props(typography.table, styles.lead)}>
                                <span>
                                  <FormattedMessage id="GEM Report" defaultMessage="GEM Report"/> {focus.year}
                                </span>
                                <span className="x1rg5ohu xxymvpz x1suov85 x16rqkct x51ohtg xqu0tyb" style={{
                backgroundColor: M.themes[theme.alias].palette[5]
            }}/>
                                {focus.lead}
                              </p>
                            </div>
                          </div>
                          <div {...stylex.props(rowStyles.linkArrow)}>
                            <motion.img {...stylex.props(rowStyles.linkImg, isRTL ? rowStyles.linkImgRtl : rowStyles.linkImgLtr)} initial="default" variants={childVariants} src={linkArrow}/>
                          </div>
                        </motion.div>
                      </Link>;
        })}

                  {theme.focuses.length > 2 && <motion.button layoutId={`expand-${theme.alias}`} onClick={()=>{
            setState((draft)=>{
                draft.expanded[theme.alias] = !state.expanded[theme.alias];
            });
        }} {...stylex.props(rowStyles.row, rowStyles.rowLink, typography.table, styles.expandButton)}>
                      <div className="x8x9d4c xack27t">
                        {state.expanded[theme.alias] ? <FormattedMessage id="See less" defaultMessage="See less"/> : <FormattedMessage id="See all" defaultMessage="See all"/>}
                      </div>
                    </motion.button>}
                </div>
              </div>
            </motion.div>;
    })}
      </motion.div>
    </div>;
}
const PublicationSelect = ({ options, value, onChange })=>{
    return <div className="xwufsc8">
      <Select.Root value={value} onValueChange={onChange}>
        <Select.Trigger {...stylex.props(typography.table, selectStyles.trigger)}>
          <Select.Value>{options.find((o)=>o.value === value)?.label}</Select.Value>
          <Select.Icon asChild={true}>
            <ArrowLeft14 className="xamitd3 x1kky2od xlup9mm x7ogvc8 x46p99k"/>
          </Select.Icon>
        </Select.Trigger>
        <Select.Content asChild={true} position="popper" sideOffset={2} className="x87ps6o x12peec7 x1tuisgq x1qspb1u xmkeg23 x1y0btm7 x1hhxfu6">
          <motion.div animate={{
        opacity: 1,
        y: 0
    }} initial={{
        opacity: 0.5,
        y: -10
    }} exit={{
        opacity: 0.5,
        y: -10
    }} transition={{
        duration: 0.5,
        type: "spring"
    }}>
            <Select.Viewport>
              {options.map((option)=><Select.Item key={option.value} value={option.value} {...stylex.props(typography.table, selectStyles.item)}>
                  {option.label}
                </Select.Item>)}
            </Select.Viewport>
          </motion.div>
        </Select.Content>
      </Select.Root>
    </div>;
};
const selectStyles = {
    trigger: {
        kfSwDN: "x87ps6o",
        koQZXg: "x8x9d4c",
        km5ZXQ: "xack27t",
        k1xSpc: "x78zum5",
        kjj79g: "x1qughib",
        kmVPX3: "xc7ga6q",
        kMwMTN: "x12klvby",
        kWkggS: "x12peec7",
        kMzoRj: "xmkeg23",
        ksu8eU: "x1y0btm7",
        kVAM5u: "x1hhxfu6",
        kaIpWk: "x12oqio5",
        kzqmXN: "xh8yej3 x7tnddx",
        k7Eaqz: "x15x72sd x10k5gof",
        kguI0P: "x1vhszaq",
        kFU1ZQ: "xt4zopc",
        kUjPgX: "xsb4npe",
        kJg5Gm: "xy8moqr",
        kZ553i: "x1b8vcwg",
        kYJ2DB: "x1s5arny",
        kGJTEo: "xah4buv",
        $$css: true
    },
    item: {
        kmVPX3: "x153ncpu",
        kMwMTN: "xjvpvmj",
        kzqmXN: "xh8yej3",
        k9WMMc: "xdpxx8g",
        k3XXqK: "x1t137rt",
        kkrTdU: "x1ypdohk",
        kRQljQ: "x9u1lb3",
        kEenPV: "xikny8b",
        $$css: true
    }
};
